import React from 'react';
import styled from 'styled-components';
import {
  BucketKind,
  PermissionKey,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  setBreakdownGroupBinning,
  setSelectedBinning,
} from '../../../../../redux/slices/oldBuilder';
import { IBasicBinning } from '../../../../../redux/slices/oldBuilder/binning/types';
import { ModalSliceType, openModal } from '../../../../../redux/slices/modal';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { SvgIcon } from '../../../../Icon';
import { convertToManualRangeBinning, isValidBinning } from '../utils';
import { isNil } from 'lodash';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import { activeSchemaIdSelector } from '../../../../../redux/selectors/builder';
import { theme } from '../../../../../styles/theme';
import { ActionButton } from '../../../../Button';
import { PERMISSION_ERROR_MESSAGE } from '../../../../Chart/constants/utils';

interface IProps {
  binning: IBasicBinning;
  groupIndex?: number;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 12px;
  padding-right: 4px;
`;

const Title = styled.div`
  font-weight: 600;
  line-height: normal;
  color: ${theme.colors.secondary900};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
`;

export default ({ binning, groupIndex }: IProps) => {
  const dispatch = useAppDispatch();
  const { checkIsPermitted } = usePermission();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const isPermittedToOpenBucketDefModal = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.BucketDefCreate,
      PermissionKey.BucketDefEdit,
    ],
    schemaId: activeSchemaId,
    validateKind: 'OR',
  });
  const isValidBinningInput = isValidBinning(binning);

  const getTitle = () => {
    if (binning.kind === BucketKind.Enum) {
      return 'Bins';
    } else if (binning.kind === BucketKind.Range) {
      return binning.isAutoRangeBucket ? 'Auto Binning' : 'Manual Binning';
    }
  };

  // method for expanding an auto range binning
  const handleSwitchToManual = () => {
    if (!(binning.kind === BucketKind.Range && binning.isAutoRangeBucket)) {
      return;
    }

    const newBinning = convertToManualRangeBinning(binning, true);

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  // method for saving current valid binning
  const handleSave = () => {
    if (!isValidBinningInput) {
      return;
    }

    dispatch(
      openModal({
        type: ModalSliceType.SaveBucketDef,
        id: '',
        data: '',
      }),
    );
  };

  return (
    <Container>
      <Title>{getTitle()}</Title>
      <Actions>
        {/* expand button only if it's auto range input */}
        {binning.kind === BucketKind.Range && binning.isAutoRangeBucket && (
          <ActionButton
            onClick={handleSwitchToManual}
            content={'Switch to Manual Binning'}
          >
            <SvgIcon
              name="expand-binning"
              width={8}
              height={8}
              customFill="currentColor"
            />
          </ActionButton>
        )}
        {/* save binning */}
        <ActionButton
          onClick={handleSave}
          content={
            isPermittedToOpenBucketDefModal
              ? 'Save Binning'
              : PERMISSION_ERROR_MESSAGE
          }
          disabled={!isValidBinningInput || !isPermittedToOpenBucketDefModal}
        >
          <SvgIcon
            name="arrow-line-down"
            width={12}
            height={12}
            customFill="currentColor"
          />
        </ActionButton>
      </Actions>
    </Container>
  );
};
